<script setup lang="ts">
const props = withDefaults(defineProps<{
  attachment: TextPasteAttachment
  variant?: 'composer' | 'preview'
}>(), {
  variant: 'composer'
})

defineEmits<{
  remove: []
}>()

const open = ref(false)
const previewLoading = ref(false)

const { height: windowHeight } = useWindowSize()

const extensionMeta = computed(() => getExtensionMeta(props.attachment.name))
const typeLabel = computed(() => extensionMeta.value.label)
const icon = computed(() => extensionMeta.value.icon)
const title = computed(() => /\.(?:txt|mdc?)$/.test(props.attachment.name) ? 'Pasted text' : 'Pasted code')
const lineCount = computed(() => props.attachment.content.split('\n').length)
const comarkContent = computed(() => attachmentToComark(props.attachment.content, props.attachment.name))
const isMarkdown = computed(() => isRenderableMarkdown(props.attachment.name))

const PANEL_HEADER_PX = 37
const PANEL_PADDING_PX = 20
const LINE_PX = 16.5

const panelHeightPx = computed(() => {
  const maxPanel = Math.min(720, windowHeight.value * 0.8)
  const maxContent = maxPanel - PANEL_HEADER_PX
  const estimatedContent = lineCount.value * LINE_PX + PANEL_PADDING_PX
  const contentHeight = Math.min(Math.max(estimatedContent, 120), maxContent)
  return contentHeight + PANEL_HEADER_PX
})

const comarkClass = computed(() => isMarkdown.value
  ? [
      'text-[13px] leading-relaxed text-default',
      '[&_*:first-child]:mt-0 [&_*:last-child]:mb-0',
      '[&_pre]:my-2 [&_pre]:rounded-md [&_pre]:border [&_pre]:border-default/50 [&_pre]:bg-muted/30 [&_pre]:p-3 [&_pre]:text-[11px] [&_pre]:leading-[1.5]'
    ].join(' ')
  : [
      'font-mono text-[11px] leading-[1.5] text-default',
      '[&_*:first-child]:mt-0 [&_*:last-child]:mb-0',
      '[&_.line]:block [&_.line]:min-w-fit',
      '[&_code]:block [&_code]:whitespace-pre',
      '[&_pre]:m-0 [&_pre]:overflow-x-auto [&_pre]:overflow-y-visible',
      '[&_pre]:rounded-none [&_pre]:border-0 [&_pre]:bg-transparent [&_pre]:p-0',
      '[&_pre]:text-[11px] [&_pre]:leading-[1.5]'
    ].join(' '))

async function openPreview() {
  if (open.value) return

  open.value = true
  previewLoading.value = true
  await nextTick()
  requestAnimationFrame(() => {
    previewLoading.value = false
  })
}
</script>

<template>
  <div class="group/chip relative w-fit min-w-0 max-w-full sm:max-w-64">
    <button
      type="button"
      class="group flex w-full min-w-0 items-center gap-2.5 rounded-lg bg-muted/40 p-1.5 pr-3.5 text-left ring ring-default/60 transition-colors outline-none hover:bg-muted/70 focus-visible:ring-2 focus-visible:ring-primary"
      :aria-label="`Preview ${title.toLowerCase()}, ${typeLabel}, ${lineCount} lines`"
      @click="openPreview"
    >
      <span class="grid size-8 shrink-0 place-items-center rounded-md bg-default ring ring-default/60">
        <UIcon
          :name="icon"
          class="col-start-1 row-start-1 size-4 transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0"
        />
        <UIcon
          name="i-lucide-maximize-2"
          class="col-start-1 row-start-1 size-3.5 text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      </span>
      <span class="flex min-w-0 flex-col gap-0.5">
        <span class="truncate text-xs/4 font-medium text-highlighted">{{ title }}</span>
        <span class="truncate text-[11px]/4 tabular-nums text-muted">
          {{ typeLabel }} · {{ lineCount }} {{ lineCount === 1 ? 'line' : 'lines' }}
        </span>
      </span>
    </button>

    <button
      v-if="variant === 'composer'"
      type="button"
      class="absolute -top-1.5 -right-1.5 grid size-4.5 place-items-center rounded-full border border-default bg-elevated text-muted transition-[opacity,color,background-color] outline-none hover:bg-accented hover:text-highlighted focus-visible:ring-2 focus-visible:ring-primary pointer-fine:opacity-0 pointer-fine:group-hover/chip:opacity-100 pointer-fine:group-focus-within/chip:opacity-100"
      aria-label="Remove attachment"
      @click="$emit('remove')"
    >
      <UIcon name="i-lucide-x" class="size-2.5" />
    </button>
  </div>

  <UModal
    v-model:open="open"
    :ui="{
      content: 'w-[calc(100vw-1rem)] max-w-3xl sm:max-w-4xl overflow-hidden rounded-xl border border-default/30 bg-default shadow-md'
    }"
  >
    <template #content>
      <div
        class="flex flex-col"
        :style="{ height: `${panelHeightPx}px` }"
      >
        <div class="flex h-[37px] shrink-0 items-center justify-between gap-3 border-b border-default/40 px-3 py-2">
          <div class="flex min-w-0 items-center gap-2">
            <UIcon :name="icon" class="size-3.5 shrink-0 text-dimmed" />
            <span class="text-xs font-medium text-highlighted">{{ typeLabel }}</span>
            <span class="font-mono text-[10px] tabular-nums text-dimmed">
              {{ lineCount }} {{ lineCount === 1 ? 'line' : 'lines' }}
            </span>
          </div>

          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="xs"
            square
            aria-label="Close"
            @click="open = false"
          />
        </div>

        <div class="relative min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain px-3 py-2.5 sm:px-4">
          <div
            v-if="previewLoading"
            class="absolute inset-0 flex items-center justify-center bg-default/80"
          >
            <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin text-dimmed" />
          </div>

          <AgentComark
            :value="comarkContent"
            :class="comarkClass"
          />
        </div>
      </div>
    </template>
  </UModal>
</template>
